/**
 * Format Service Factory
 * Provides singleton instance with locale detection and management
 * Syncs with i18n service for language changes
 * @module @infra/format
 */

import { getI18nService, getLanguageLocale, LANGUAGE_INFO } from '@infra/i18n';
import type { FormatService, CurrencyFormatOptions, CollatorOptions } from './format.types';
import { formatCurrency } from './format.currency';
import { compareStrings, sortStrings } from './format.sorting';

/**
 * FormatService implementation.
 *
 * Currency and collation only — see `FormatService` in format.types.ts for why
 * date/time formatting deliberately does not live behind this singleton.
 */
class FormatServiceImpl implements FormatService {
  private locale: string;

  /**
   * Initialize with detected or provided locale
   */
  constructor(initialLocale?: string) {
    this.locale = this.detectLocale(initialLocale);
    this.setupI18nSync();
  }

  /**
   * Detect locale from:
   * 1. Override parameter
   * 2. the i18n service's resolved language
   * 3. navigator.language (browser default)
   * 4. Fallback to 'en' (English)
   */
  private detectLocale(override?: string): string {
    if (override) return override;

    // The i18n service, not `localStorage('language')`. That key is only written once
    // the user picks a language explicitly, so on a navigator-detected first visit it
    // is empty and this fell through to `navigator.language` — which is exactly the
    // input i18n had already run through its own allowlist and default. The two then
    // disagreed, and dates rendered in a different locale than the UI. `getLanguage()`
    // is resolved in the service's constructor, so it is safe to read before boot.
    try {
      const language = getI18nService().getLanguage();
      if (language) return getLanguageLocale(language);
    } catch {
      // i18n unavailable (SSR, or a partially initialised module graph).
    }

    // Try navigator. Keep the full BCP-47 tag — `Intl` reads the region, and dropping
    // it turns `zh-HK` into `zh`, which Intl resolves to mainland conventions
    // (Simplified, pinyin collation) rather than Hong Kong's.
    if (typeof navigator !== 'undefined' && navigator.language) {
      return navigator.language;
    }

    // Last resort: the table's English locale, not a bare `en` literal, so this agrees
    // with what `getLanguageLocale` hands back everywhere else.
    return LANGUAGE_INFO.en.locale;
  }

  /**
   * Setup sync with i18n service
   * Listen for language changes and update locale accordingly
   */
  private setupI18nSync(): void {
    if (typeof window === 'undefined') return;

    const handleLanguageChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ language: string }>;
      if (customEvent.detail?.language) {
        this.setLocale(customEvent.detail.language);
      }
    };

    window.addEventListener('i18n:languageChange', handleLanguageChange);
  }

  /**
   * Get current locale
   */
  getLocale(): string {
    return this.locale;
  }

  /**
   * Set current locale. Persists to localStorage by default; pass `persist=false` for
   * a pure in-memory alignment (e.g. boot-sync from the i18n language) that must not
   * write the `'language'` key.
   */
  setLocale(locale: string, persist = true): void {
    this.locale = locale;
    if (persist) {
      try {
        localStorage.setItem('language', locale);
      } catch {
        // localStorage not available
      }
    }
  }

  /**
   * Format number as currency
   */
  formatCurrency(value: number, options: CurrencyFormatOptions): string {
    return formatCurrency(value, this.locale, options);
  }

  /**
   * Compare strings respecting locale rules
   */
  compareStrings(a: string, b: string, options?: CollatorOptions): number {
    return compareStrings(a, b, this.locale, options);
  }

  /**
   * Sort strings array respecting locale rules
   */
  sortStrings(strings: string[], options?: CollatorOptions): string[] {
    return sortStrings(strings, this.locale, options);
  }
}

// Singleton instance
let instance: FormatService | null = null;

/**
 * Get or create FormatService singleton
 */
export function getFormatService(): FormatService {
  if (!instance) {
    instance = new FormatServiceImpl();
  }
  return instance;
}

/**
 * FormatService singleton instance (auto-initialized on import)
 */
export const formatService = getFormatService();
